<template>
  <div class="about-page">
    <!-- Hero -->
    <section
      class="relative flex items-center justify-center text-center pt-32 pb-20 overflow-hidden"
    >
      <!-- BG image -->
      <div
        class="absolute inset-0 -z-10 bg-cover bg-center"
        :style="{
          backgroundImage: `url(${require('@/assets/AboutUs/hero.jpg')})`,
        }"
      ></div>

      <!-- Themed overlay (dark: strong, light: subtle/none via --menu-hero-overlay) -->
      <div class="absolute inset-0 hero-overlay pointer-events-none"></div>

      <!-- Content -->
      <div class="text-center px-6 py-10 max-w-4xl mx-auto z-10">
        <h1
          class="font-['DM_Serif_Display'] about-hero-title text-2xl sm:text-3xl md:text-3xl lg:text-4xl mb-4"
        >
          {{ $t("about.heroTitle") }}
        </h1>
        <p
          class="about-hero-subtitle font-['DM_Sans'] text-sm sm:text-base md:text-lg lg:text-xl font-light"
        >
          {{ $t("about.heroSubtitle") }}
        </p>
      </div>
    </section>

    <!-- Story -->
    <section class="py-24 about-section">
      <div class="container mx-auto px-5">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <!-- Images -->
          <div class="relative">
            <div
              class="grid grid-cols-2 grid-rows-2 h-64 sm:h-80 md:h-[400px] lg:h-[350px] xl:h-[400px] gap-0"
            >
              <div
                class="relative overflow-hidden shadow-xl shadow-black/30 image-1"
              >
                <img
                  src="../assets/AboutUs/story1.png"
                  alt="Rustic kitchen pantry"
                  class="w-full h-full object-cover"
                />
              </div>
              <div
                class="relative overflow-hidden shadow-xl shadow-black/30 image-2"
              >
                <img
                  src="../assets/AboutUs/story2.png"
                  alt="Chef preparing food"
                  class="w-full h-full object-cover"
                />
              </div>
              <div
                class="relative overflow-hidden shadow-xl shadow-black/30 image-3"
              >
                <img
                  src="../assets/AboutUs/story3.png"
                  alt="Chef cooking with flames"
                  class="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          <!-- Copy -->
<div class="p-8 about-copy">
  <p class="text-sm sm:text-base md:text-lg leading-relaxed mb-4">
    {{ $t("about.section1.p1") }}
  </p>
  <p class="text-sm sm:text-base md:text-lg leading-relaxed mb-4">
    {{ $t("about.section1.p2") }}
  </p>
  <p class="text-sm sm:text-base md:text-lg leading-relaxed">
    {{ $t("about.section1.p3") }}
  </p>
</div>

        </div>
      </div>
    </section>

    <!-- Chefs -->
    <section class="py-12 about-section">
      <div class="container mx-auto px-5">
        <h2
          class="font-dm-serif-display text-center mb-16 tracking-wider text-xl sm:text-2xl md:text-3xl lg:text-3xl section-heading"
        >
          {{ $t("about.chefsTitle") }}
        </h2>

        <div
          class="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-3 gap-8 mt-12 max-w-6xl mx-auto justify-items-center"
        >
          <!-- card 1 -->
          <div class="chef-card">
            <div class="avatar">
              <img
                src="../assets/AboutUs/chef1.png"
                alt="Chef Omar El-Sayed"
                class="w-full h-full object-cover"
              />
            </div>
            <h3
              class="chef-name font-playfair-display font-bold mb-4 tracking-wide"
            >
              {{ $t("about.chefs.0.name") }}
            </h3>
            <p class="chef-desc font-dm-sans font-normal leading-relaxed">
              {{ $t("about.chefs.0.description") }}
            </p>
          </div>

          <!-- card 2 -->
          <div class="chef-card">
            <div class="avatar">
              <img
                src="../assets/AboutUs/chef2.png"
                alt="Chef Mahmoud Youssef"
                class="w-full h-full object-cover"
              />
            </div>
            <h3
              class="chef-name font-playfair-display font-bold mb-4 tracking-wide"
            >
              {{ $t("about.chefs.1.name") }}
            </h3>
            <p class="chef-desc font-dm-sans font-normal leading-relaxed">
              {{ $t("about.chefs.1.description") }}
            </p>
          </div>

          <!-- card 3 -->
          <div class="chef-card">
            <div class="avatar">
              <img
                src="../assets/AboutUs/chef3.png"
                alt="Chef Lina Hafez"
                class="w-full h-full object-cover"
              />
            </div>
            <h3
              class="chef-name font-playfair-display font-bold mb-4 tracking-wide"
            >
              {{ $t("about.chefs.2.name") }}
            </h3>
            <p class="chef-desc font-dm-sans font-normal leading-relaxed">
              {{ $t("about.chefs.2.description") }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <SmartChatbot />
  </div>
</template>

<script setup>
import SmartChatbot from "@/components/SmartChatbot.vue";
</script>

<style scoped>
/* hero */
.hero-overlay {
  background: var(--menu-hero-overlay);
}
.about-hero-title {
  color: var(--about-hero-title);
}
.about-hero-subtitle {
  color: var(--about-hero-subtitle);
}

/* sections */
.about-section {
  background: var(--about-section-bg);
}
.about-copy {
  color: var(--about-section-text);
}
.section-heading {
  color: var(--about-hero-title);
  font-weight: 400;
}

.chef-card {
  background: var(--about-chef-card-bg);
  border: 1px solid var(--about-chef-card-border);
  border-radius: 1rem;
  padding: 2rem; /* was 1.5rem */
  text-align: center;
  backdrop-filter: blur(8px); /* stronger blur for elegance */
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  min-height: 55%;
  width: 22rem; /* was 18rem → wider card */
}
.chef-card:hover {
  transform: translateY(-12px);
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.15);
}

.avatar {
  width: 9rem; /* bigger circle */
  height: 9rem; /* was 7rem */
  margin: 0 auto 2rem; /* more spacing below */
  border-radius: 9999px;
  overflow: hidden;
  border: 5px solid var(--about-chef-card-border);
  box-shadow: 0 12px 25px rgba(0, 0, 0, 0.18);
}

.chef-name {
  color: var(--about-chef-name);
  font-size: 1.25rem; /* ~20px */
  line-height: 1.6;
}

.chef-desc {
  color: var(--about-chef-description);
  font-size: 1rem; /* ~16px */
  line-height: 1.75;
  max-width: 18rem;
  margin: 0 auto;
}

/* existing helpers */
.text-shadow-md {
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

/* grid placement like your original */
.image-1 {
  grid-area: 1 / 1 / 3 / 2;
}
.image-2 {
  grid-area: 1 / 2 / 2 / 3;
}
.image-3 {
  grid-area: 2 / 2 / 3 / 3;
}

@media (max-width: 768px) {
  .h-\[600px\] {
    height: 400px;
  }
}
</style>

<style>
@import url("https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=DM+Serif+Display&display=swap");
.font-dm-serif-display {
  font-family: "DM Serif Display", serif;
}
.font-dm-sans {
  font-family: "DM Sans", sans-serif;
}
</style>
